/* eslint-disable array-callback-return */
// REACT IMPORTS
import React, { useEffect, useState } from "react";
// COMPONENT IMPORTS

import { useAppDispatch } from "store/hooks";
import Skeleton from "react-loading-skeleton";

// ICONS IMPORTS
import { CloseIcon } from "icons/payments/CloseIcon";
import { EyeIcon } from "icons/payments/EyeIcon";
import QuestionsFooter from "components/auth/QuestionsFooter";
import { formatCurrency, rowHelperText } from "utils/HelperFunctions";
import moment from "moment";
import { getPaymentCustomPlan, useLeadData } from "store/slices/dashboardSlice";
import { useUserData } from "store/slices/authSlice";
import { useTid } from "utils/testid";
import {
  getScheduleDisplayAmount,
  getTransactionFeeForPaymentMethod,
} from "utils/updatePaymentPlanUtils";

// PROPS TYPE
interface PlanPreviewProps {
  id?: string;
  setId?: any;
  show: boolean;
  setShow: any;
  schedule?: any;
  paymentMethodToken?: string;
  isLoading?: boolean;
  scheduleMethod: string;
  setHandleDrawerSize?: (value: number) => void;
  isStax?: boolean;
}

const getPaymentMethodCode = (method: string) => {
  switch (method) {
    case "Bank":
      return "ACH";
    case "PayPal":
      return "PP";
    case "Cash App Pay":
      return "CAPP";
    case "Amazon Pay":
      return "AMZP";
    case "Google Pay":
      return "GP";
    case "Apple Pay":
      return "AP";
    case "Wire":
      return "WTO";
    case "Card":
      return "CC";
    case "CC":
    case "ACH":
    case "PP":
    case "CAPP":
    case "AMZP":
    case "GP":
    case "AP":
    case "WTO":
      return method;
    default:
      return "CC";
  }
};

/* ------------------------------------- PAYMENT PlAN PREVIEW ------------------------------------- */

const PlanPreview = (props: PlanPreviewProps) => {
  const tid = useTid();
  const {
    show,
    setShow,
    schedule,
    isLoading,
    scheduleMethod,
    setHandleDrawerSize = () => {},
    paymentMethodToken = "",
    isStax = false,
  } = props;

  const [isFeeLoading, setIsFeeLoading] = useState(true);
  const accountLeadData = useLeadData();
  const userData = useUserData();
  const dispatch = useAppDispatch();
  const [data, setData] = useState<any>({});
  useEffect(() => {
    if (!show) {
      onClose();
    } else {
      handleGetConvFee();
    }
  }, [show, scheduleMethod]);

  const onClose = () => {
    setShow(false);
    setHandleDrawerSize(0);
    setIsFeeLoading(true);
  };
  const handleGetConvFee = async () => {
    const transactions = schedule?.transactions || [];
    const paymentMethodCode = getPaymentMethodCode(scheduleMethod);
    const toPreviewRow = (itm: any, index: number) => ({
      amount: itm?.amount,
      scheduled_amount: getScheduleDisplayAmount(
        itm,
        index,
        schedule?.percentage_amounts || [],
        schedule?.transaction_type || "",
        transactions
      ),
      balance: itm?.balance,
      fee: getTransactionFeeForPaymentMethod(itm, paymentMethodCode),
      status: itm?.status,
      payment_date: itm?.billing_date || itm?.date || itm?.payment_date,
    });
    const previewRows = transactions.map(toPreviewRow);

    // Already-processed installments keep their stored fee, date and amount;
    // only pending installments are re-quoted against the selected method.
    const processedRows = previewRows.filter(
      (itm: any) => itm?.status && itm?.status !== "Pending"
    );
    const pendingRows = previewRows.filter(
      (itm: any) => !itm?.status || itm?.status === "Pending"
    );

    let recalculatedPending = pendingRows;
    try {
      if (pendingRows.length) {
        const pendingFeeSubtotal = pendingRows.reduce(
          (sum: number, row: any) => sum + (Number(row?.fee) || 0),
          0
        );
        const payload = {
          date_array: pendingRows.map((row: any) => ({
            amount: row?.amount,
            balance: row?.balance,
            fee: row?.fee,
            status: row?.status,
            payment_date: row?.payment_date,
          })),
          lead_id: accountLeadData?.lead_id,
          cluster_id: userData?.data?.accountData?.clusterid,
          balance: userData?.data?.accountData?.balance,
          schedule_amount: (
            schedule?.schedule_amount || "custom"
          )?.toLowerCase(),
          payment_method: paymentMethodCode,
          no_of_transaction: pendingRows.length,
          grand_total: schedule?.grand_total || "0.00",
          fee_subtotal: pendingFeeSubtotal.toFixed(2),
          amount_total: schedule?.amount_subtotal || "0.00",
          start_date: pendingRows[0]?.payment_date || schedule?.start_date,
          schedule_fld: schedule?.schedule_fld || "daily",
          amount: schedule?.amount || "0.00",
          transaction_type: schedule?.transaction_type || "amount",
          make_payment: true,
          isAmount: true,
          isDelete: false,
          state: userData?.data?.accountData?.state || "",
          custom_conv: "on",
          pm_id: paymentMethodToken,
        };

        const res: any = await dispatch(getPaymentCustomPlan(payload)).unwrap();
        if (res?.body?.success && Array.isArray(res?.body?.data?.date_array)) {
          recalculatedPending = res.body.data.date_array.map(
            (row: any, idx: number) => {
              const recalculatedRow = {
                ...pendingRows[idx],
                ...row,
                status: pendingRows[idx]?.status ?? "Pending",
                scheduled_amount: pendingRows[idx]?.scheduled_amount ?? 0,
              };

              return {
                ...recalculatedRow,
                fee: getTransactionFeeForPaymentMethod(
                  recalculatedRow,
                  paymentMethodCode
                ),
              };
            }
          );
        }
      }
    } catch (error) {
      console.log("handleGetConvFee error =====", error);
    }

    const mergedRows = [...processedRows, ...recalculatedPending].sort(
      (a: any, b: any) => {
        const aTime = new Date(a?.payment_date).getTime();
        const bTime = new Date(b?.payment_date).getTime();
        if (Number.isNaN(aTime) || Number.isNaN(bTime)) return 0;
        return aTime - bTime;
      }
    );
    const feeSubtotal = mergedRows.reduce(
      (sum: number, row: any) => sum + (Number(row?.fee) || 0),
      0
    );
    const amountTotal = mergedRows.reduce(
      (sum: number, row: any) => sum + (Number(row?.scheduled_amount) || 0),
      0
    );

    setData({
      ...schedule,
      transaction_type: schedule?.transaction_type,
      date_array: mergedRows,
      fee_subtotal: feeSubtotal.toFixed(2),
      amount_total: amountTotal.toFixed(2),
      grand_total: (amountTotal + feeSubtotal).toFixed(2),
    });
    setIsFeeLoading(false);
  };

  return (
    <div
      className="bg-bgSecondary h-full overflow-y-auto  fixed w-full top-0 right-0 z-50 shadow-[0_10px_100px_0_#3C425733]"
      data-testid={tid("plan-preview")}
      aria-label="Payment plan preview"
    >
      <div
        className="lg:px-10 px-5 py-5 bg-bgPrimary flex items-center justify-between  top-0 w-full z-30"
        data-testid={tid("plan-preview-header")}
      >
        <p className="flex items-center gap-2 lg:text-[26px] text-[22px] text-txtPrimary font-medium leading-[30px]">
          <EyeIcon
            aria-hidden="true"
            strokeColor="var(--color-txt-primary)"
            width="24"
            height="24"
          />
          Preview
        </p>

        <div className="flex items-center gap-5">
          <div
            role="button"
            tabIndex={0}
            aria-label="Close payment plan preview"
            data-testid={tid("plan-preview-close-button")}
            onClick={() => onClose()}
            className="cursor-pointer p-2 rounded-full hover:!bg-bgSecondary"
          >
            <CloseIcon aria-hidden="true" />
          </div>
        </div>
      </div>
      <div className="lg:px-10 px-5 py-4 flex items-center justify-between">
        <div className="lg:text-xl text-lg font-normal leading-normal text-txtPrimary">
          Payment Schedule
        </div>
      </div>
      <div className="w-full lg:px-10 px-5 py-4 items-start justify-start gap-1 flex flex-col">
        <p className="w-full text-xs font-normal text-txtSecondary">AMOUNT</p>
        <p className="w-full text-2xl text-txtPrimary">
          {isFeeLoading ? (
            <Skeleton className="w-full" />
          ) : (
            formatCurrency(data.grand_total) || "$0.00"
          )}
        </p>
      </div>
      {data?.date_array?.length && !isFeeLoading ? (
        <div className="lg:px-10 px-5 py-2 animate__animated animate__fadeIn">
          <div className="flex flex-col items-start gap-4 w-full">
            <div className="lg:px-6 px-4 py-5 bg-bgPrimary border border-strokePrimary rounded-lg w-full overflow-x-scroll">
              <div className="flex flex-col items-start gap-5">
                <div className="flex items-center justify-between w-full">
                  <div className="text-sm leading-normal font-bold uppercase text-txtPrimary">
                    SCHEDULE
                  </div>
                </div>
                <div className="h-[1px] w-full bg-strokePrimary min-w-[230px]" />
                <div className="flex items-center w-full justify-between">
                  <div
                    className={`${
                      data.fee_subtotal > 0 ? "w-1/4" : "w-1/3"
                    } min-w-[35px] text-sm text-txtParagraph leading-normal text-start`}
                  >
                    #
                  </div>
                  <div
                    className={`${
                      data.fee_subtotal > 0 ? "w-1/4" : "w-1/3"
                    } min-w-[96px] text-sm text-txtParagraph leading-normal text-start`}
                  >
                    Payment Date
                  </div>

                  {isLoading || isFeeLoading ? (
                    <div className="w-[20%] min-w-[56px] text-sm text-txtParagraph leading-normal text-center">
                      <Skeleton width={100} />
                    </div>
                  ) : (
                    <>
                      {data.fee_subtotal > 0 ? (
                        <div
                          className={`${
                            data.fee_subtotal > 0 ? "w-[20%]" : "w-1/3"
                          } min-w-[56px] text-sm text-txtParagraph leading-normal text-center`}
                        >
                          {isStax ? "Surcharge" : "Fee"}
                        </div>
                      ) : null}
                    </>
                  )}

                  <div
                    className={`${
                      data.fee_subtotal > 0 ? "w-[20%]" : "w-1/3"
                    } min-w-[96px] text-sm text-txtParagraph leading-normal text-end`}
                  >
                    Amount
                  </div>
                </div>
                <div className="h-[1px] w-full bg-strokePrimary min-w-[230px]" />
                {data?.date_array?.length
                  ? data?.date_array?.map((x: any, idx: number) => {
                      const scheduleRowId =
                        x?.id || x?.payment_date || x?.billing_date || idx + 1;
                      return (
                        <div
                          className="flex items-center w-full justify-between"
                          key={scheduleRowId}
                          data-testid={tid(
                            `plan-preview-schedule-row-${scheduleRowId}`
                          )}
                        >
                          <div
                            className={`${
                               data.fee_subtotal > 0 ? "w-1/4" : "w-1/3"
                            } min-w-[35px] text-sm text-txtPrimary leading-normal text-start flex gap-[6px] items-center`}
                          >
                            {`${idx + 1}` + rowHelperText(idx + 1)}
                          </div>
                          <div
                            className={`${
                               data.fee_subtotal > 0 ? "w-1/4" : "w-1/3"
                            }  min-w-[96px] text-sm text-txtPrimary leading-normal text-start flex gap-[6px] items-center`}
                          >
                            {x?.payment_date
                              ? moment(x?.payment_date).format("MM/DD/YYYY")
                              : x?.billing_date
                                ? moment(x?.billing_date).format("MM/DD/YYYY")
                                : "--"}
                          </div>

                          { data.fee_subtotal > 0 ? (
                            <div
                              className={`${
                                 data.fee_subtotal > 0 ? "w-[20%]" : "w-1/3"
                              } min-w-[56px] text-sm text-txtPrimary leading-normal text-center`}
                            >
                              { data.fee_subtotal > 0 ? (
                                isLoading || isFeeLoading ? (
                                  <Skeleton width={100} />
                                ) : (
                                  formatCurrency(x.fee)
                                )
                              ) : (
                                "$0.00"
                              )}
                            </div>
                          ) : null}
                          <div
                            className={`${
                               data.fee_subtotal > 0 ? "w-[20%]" : "w-1/3"
                            } min-w-[96px] text-sm text-txtPrimary leading-normal text-end`}
                          >
                            {formatCurrency(x?.scheduled_amount || 0) ??
                              "$0.00"}
                          </div>
                        </div>
                      );
                    })
                  : null}
                <div className="h-[1px] w-full bg-strokePrimary min-w-[230px]" />
                <div className="flex items-center w-full justify-between min-w-[230px]">
                  <div
                    className={`${
                      data.fee_subtotal > 0 ? "w-[23%]" : "w-1/2"
                    } text-xs font-medium text-txtPrimary`}
                  >
                    SUBTOTAL
                  </div>
                  <div
                    className={`${data.fee_subtotal > 0 ? "w-[25%]" : "w-0"}`}
                  ></div>
                  {data.fee_subtotal > 0 ? (
                    <div
                      className={`${
                        data.fee_subtotal > 0 ? "w-[20%]" : "w-1/3"
                      } text-center text-txtPrimary font-bold leading-normal`}
                    >
                      {isLoading || isFeeLoading ? (
                        <Skeleton width={100} />
                      ) : (
                        formatCurrency(data.fee_subtotal) || "0.00"
                      )}
                    </div>
                  ) : null}
                  <div
                    className={`${
                      data.fee_subtotal > 0 ? "w-[20%]" : "w-1/2"
                    } text-end text-txtPrimary font-bold leading-normal`}
                  >
                    {isLoading || isFeeLoading ? (
                      <Skeleton width={100} />
                    ) : (
                      formatCurrency(data.amount_total) || "0.00"
                    )}
                  </div>
                </div>
                <div className="h-[1px] w-full bg-strokePrimary min-w-[230px]" />
                <div className="flex items-start justify-between w-full min-w-[230px]">
                  <div className="text-sm leading-normal font-bold uppercase text-txtPrimary">
                    Total
                  </div>
                  <div className="text-lg leading-normal font-bold text-txtPrimary">
                    {isLoading || isFeeLoading ? (
                      <Skeleton width={100} />
                    ) : (
                      formatCurrency(data.grand_total) || "0.00"
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="lg:px-10 px-5 py-2 animate__animated animate__fadeIn">
          <div className="flex flex-col items-start gap-4 w-full">
            <div className="lg:px-6 px-4 py-5 bg-bgPrimary border border-strokePrimary rounded-lg w-full overflow-x-scroll">
              <div className="flex flex-col items-start gap-5">
                <div className="flex items-center justify-between w-full">
                  <div className="text-sm leading-normal font-bold uppercase text-txtPrimary">
                    SCHEDULE
                  </div>
                </div>
                <div className="h-[1px] w-full bg-strokePrimary min-w-[230px]" />
                <div className="flex items-center w-full justify-between">
                  <div
                    className={`w-1/4 min-w-[35px] text-sm text-txtParagraph leading-normal text-start`}
                  >
                    #
                  </div>
                  <div
                    className={`w-1/4 min-w-[96px] text-sm text-txtParagraph leading-normal text-start`}
                  >
                    Payment Date
                  </div>

                  {isLoading || isFeeLoading ? (
                    <div className="w-[20%] min-w-[56px] text-sm text-txtParagraph leading-normal text-center">
                      <Skeleton width={100} />
                    </div>
                  ) : (
                    <>
                      {data.fee_subtotal > 0 ? (
                        <div
                          className={`w-[20%] min-w-[56px] text-sm text-txtParagraph leading-normal text-center`}
                        >
                          {isStax ? "Surcharge" : "Fee"}
                        </div>
                      ) : null}
                    </>
                  )}

                  <div
                    className={`w-[20%] min-w-[96px] text-sm text-txtParagraph leading-normal text-end`}
                  >
                    Amount
                  </div>
                </div>
                <div className="h-[1px] w-full bg-strokePrimary min-w-[230px]" />
                {[1, 2, 3, 4, 5].map((x: any, idx: number) => {
                  return (
                    <div
                      className="flex items-center w-full justify-between"
                      key={idx}
                      data-testid={tid(`plan-preview-loading-row-${idx + 1}`)}
                    >
                      <div
                        className={`w-[25%] min-w-[35px] text-sm text-txtPrimary leading-normal text-start flex gap-[6px] items-center`}
                      >
                        {`${idx + 1}` + rowHelperText(idx + 1)}
                      </div>
                      <div
                        className={`w-[25%] min-w-[96px] text-sm text-txtPrimary leading-normal text-start flex gap-[6px] items-center`}
                      >
                        <Skeleton className="w-[25%] min-w-[96px]" />
                      </div>

                      <div
                        className={`w-[20%] min-w-[56px] text-sm text-txtPrimary leading-normal text-left`}
                      >
                        <Skeleton className="w-full" />
                      </div>

                      <div
                        className={`w-[20%] min-w-[96px] text-sm text-txtPrimary leading-normal text-end`}
                      >
                        <Skeleton className="w-full" />
                      </div>
                    </div>
                  );
                })}
                <div className="h-[1px] w-full bg-strokePrimary min-w-[230px]" />
                <div className="flex items-center w-full justify-between min-w-[230px]">
                  <div
                    className={`${
                      data.fee_subtotal > 0 ? "w-[23%]" : "w-1/2"
                    } text-xs font-medium text-txtPrimary`}
                  >
                    SUBTOTAL
                  </div>
                  <div
                    className={`${data.fee_subtotal > 0 ? "w-[25%]" : "w-0"}`}
                  ></div>
                  <Skeleton width={100} />
                  <div
                    className={`w-[20%] text-end text-txtPrimary font-bold leading-normal`}
                  >
                    <Skeleton width={100} />
                  </div>
                </div>
                <div className="h-[1px] w-full bg-strokePrimary min-w-[230px]" />
                <div className="flex items-start justify-between w-full min-w-[230px]">
                  <div className="text-sm leading-normal font-bold uppercase text-txtPrimary">
                    Total
                  </div>
                  <div className="text-lg leading-normal font-bold text-txtPrimary">
                    <Skeleton width={100} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* In normal flow (like the other preview drawers) so a long schedule
          scrolls past it instead of underneath it. */}
      <div className={`py-5 lg:px-10 px-5`}>
        <QuestionsFooter isPreview={true} />
      </div>
    </div>
  );
};

export default PlanPreview;
